import { useParams, Link } from 'react-router-dom'
import { useState } from 'react'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import SEOHead from '@/components/feature/SEOHead'
import Breadcrumb from '@/components/feature/Breadcrumb'
import { comics } from '@/mocks/comics'

const categoryNameMap: Record<string, string> = {
  love: '恋爱', fantasy: '奇幻', action: '动作', suspense: '悬疑',
  comedy: '搞笑', horror: '惊悚', daily: '日常', history: '古风',
}

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<'chapters' | 'comments'>('chapters')

  const comic = comics.find((c) => c.id === Number(id))

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 韩漫画网" description="您查找的漫画不存在或已被移除。" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-600 mb-6">您查找的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap inline-flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    )
  }

  const categoryName = categoryNameMap[comic.category] || comic.category
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `http://eocesp.cn/comic/${comic.id}`,
    image: comic.coverImage,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: Math.floor(Number(comic.views.replace(/[^0-9]/g, '')) / 100),
    },
  }

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩漫画网 - 免费在线观看`}
        description={`${comic.title}，作者${comic.author}。${comic.description.slice(0, 120)}... 韩漫画网免费在线观看。`}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫,${categoryName}漫画,免费漫画在线阅读`}
        canonicalUrl={`http://eocesp.cn/comic/${comic.id}`}
        ogType="book"
        ogImage={comic.coverImage}
        schema={schema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 max-w-[1400px] mx-auto py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${categoryName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="pb-10 md:pb-14">
          <div className="px-4 md:px-6 max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="w-full lg:w-[320px] shrink-0">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 flex gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="flex-1 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="w-12 h-12 flex items-center justify-center rounded-lg border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-foreground-600 text-lg"></i>
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center rounded-lg border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-foreground-600 text-lg"></i>
                  </button>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-start gap-4 mb-4">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 flex-1">{comic.title}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '已完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 md:gap-5 mb-6">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-line text-foreground-500 text-sm"></i>
                    <span className="text-sm text-foreground-700">{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-folder-line text-foreground-500 text-sm"></i>
                    <Link to={`/category/${comic.category}`} className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">
                      {categoryName}
                    </Link>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm text-foreground-700 font-semibold">{comic.rating}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-500 text-sm"></i>
                    <span className="text-sm text-foreground-700">{comic.views}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-8">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-1 mb-5 bg-background-100 rounded-full p-1">
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'chapters' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    章节列表 ({comic.chapters.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('comments')}
                    className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'comments' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    读者评论 ({comic.comments.length})
                  </button>
                </div>

                {activeTab === 'chapters' && (
                  <div className="space-y-2">
                    {comic.chapters.map((chapter) => (
                      <Link
                        key={chapter.num}
                        to={`/comic/${comic.id}`}
                        className="flex items-center justify-between px-4 py-3.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-500">第{chapter.num}话</span>
                          <span className="text-sm text-foreground-900 font-medium">{chapter.title}</span>
                        </div>
                        <time dateTime={chapter.date} className="text-xs text-foreground-500 whitespace-nowrap">{chapter.date}</time>
                      </Link>
                    ))}
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div className="space-y-4">
                    {comic.comments.map((comment, index) => (
                      <div key={index} className="bg-background-100 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <img
                            src={comment.avatar}
                            alt={comment.user}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="text-sm font-semibold text-foreground-900">{comment.user}</p>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }, (_, i) => (
                                <i
                                  key={i}
                                  className={`text-xs ${i < comment.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                                ></i>
                              ))}
                            </div>
                          </div>
                          <time className="ml-auto text-xs text-foreground-500 whitespace-nowrap">{comment.date}</time>
                        </div>
                        <p className="text-sm text-foreground-700 leading-relaxed">{comment.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}